import { ChevronLeft, ChevronRight } from "lucide-react";
import { useCourseModule } from "../Hooks/useCourseModule";
import { CONTENT_TYPE } from "../constants";
import { memo } from "react";
function CoursePlayer({
  moduleId,
  courseId,
  onPrev,
  onNext,
  hasPrev,
  hasNext,
  onCompleted,
}) {
  const { module, loading, markComplete } = useCourseModule(courseId, moduleId);

  const handleMarkComplete = async () => {
    const success = await markComplete();
    if (success) {
      onCompleted(moduleId);
    }
  };

  if (loading || !module) {
    return (
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl text-white flex flex-col w-full">
        <div className="flex justify-between items-center px-6 py-4 border-b border-white/10">
          <div className="space-y-2">
            <div className="h-5 w-28 bg-gray-700 rounded" />
            <div className="h-6 w-48 bg-gray-700 rounded" />
          </div>
          <div className="h-6 w-20 bg-gray-700 rounded-full" />
        </div>

        <div className="flex-1 bg-gray-800 flex items-center justify-center overflow-hidden">
          <div className="w-full h-full max-w-[1280px] aspect-video flex items-center justify-center text-gray-400">
            {loading && "Loading content..."}
            {!loading && !module && "Failed to load module"}
          </div>
        </div>

        <div className="flex justify-between px-6 py-4 border-t border-white/10">
          <div className="h-9 w-24 bg-gray-700 rounded" />
          <div className="h-9 w-24 bg-gray-700 rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl text-white flex flex-col mb-9 lg:mb-0">
      {/* Header */}
      <div className="flex items-center justify-between w-full px-4 py-2 border-b border-white/10">
        {/* Title */}
        <div className="max-w-[70%] sm:max-w-[75%]">
          <p className="text-xs text-gray-300">Module {module.order}</p>
          <h2 className="text-base font-semibold leading-snug overflow-hidden text-ellipsis [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2] sm:[-webkit-line-clamp:1]">
            {module.title}
          </h2>
        </div>

        {module.is_completed ? (
          <span className="self-start sm:self-center bg-white text-black flex items-center justify-center px-3 py-1.5 rounded-full text-xs flex-shrink-0">
            Completed
          </span>
        ) : (
          <span className="self-start sm:self-center bg-white text-black px-3 py-1.5 flex items-center justify-center  rounded-full text-xs flex-shrink-0">
            In Progress
          </span>
        )}
      </div>
      <div className="flex-1 bg-black flex items-center justify-center lg:w-full overflow-hidden">
        <div className="w-full h-full max-w-[1280px] aspect-video">
          {renderContent(module)}
        </div>
      </div>
      {/* Footer */}
      <div className="flex justify-between px-6 p-2 border-t border-white/10">
        <button
          onClick={onPrev}
          disabled={!hasPrev}
          className={`px-2 p-1 rounded flex justify-center items-center text-xs ${
            hasPrev ? "bg-white text-black" : "bg-white/40 text-black/50"
          }`}
        >
          <ChevronLeft size={10} strokeWidth={2} />
          Previous
        </button>
        {!module.is_completed && (
          <button
            onClick={handleMarkComplete}
            disabled={module.is_completed}
            className="px-2 p-1 border border-white/30 rounded text-xs"
          >
            {"Mark as Complete"}
          </button>
        )}
        <button
          onClick={onNext}
          disabled={!hasNext}
          className={`px-2 p-1 rounded flex justify-center items-center text-xs ${
            hasNext ? "bg-white text-black" : "bg-white/40 text-black/50"
          }`}
        >
          Next <ChevronRight size={10} strokeWidth={2} />
        </button>
      </div>
    </div>
  );
}
/// render the respective media
function renderContent(module) {
  switch (module.content_type) {
    case CONTENT_TYPE.VIDEO:
      return (
        <video
          controls
          preload="metadata"
          className="w-full h-full object-contain bg-black"
        >
          <source src={module.file} />
          Your browser does not support the video tag.
        </video>
      );

    case CONTENT_TYPE.PDF:
      return (
        <iframe
          src={module.file}
          title="PDF Viewer"
          className="w-full h-full border-0 bg-white"
        />
      );

    case CONTENT_TYPE.PPT:
    case CONTENT_TYPE.PPTX:
      return (
        <iframe
          src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
            module.file,
          )}`}
          title="PPT Viewer"
          className="w-full h-full border-0"
        />
      );

    case CONTENT_TYPE.TEXT:
      return (
        <iframe
          src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
            module.file,
          )}`}
          title="Document Viewer"
          className="w-full h-full border-0 bg-white"
        />
      );

    default:
      return (
        <div className="w-full h-full flex items-center justify-center text-gray-400">
          Unsupported content type
        </div>
      );
  }
}

export default memo(CoursePlayer);
